import { Box, Grid, Divider, Typography, Paper, Alert, Stack, styled, TextField, Checkbox, FormControlLabel, FormHelperText } from "@mui/material";
import { RichContent } from "@gisce/oficina-virtual-components";
import { FC, useContext, useEffect } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";

import AddressForm from "@/components/form-fields/AddressForm/AddressForm";
import CheckboxField from "../../form-fields/CheckboxField";
import EmailField, { validEmail } from "../../form-fields/EmailField";
import { validateFileUpload } from "../../form-fields/FileUploadField";
import LangSelectField from "../../form-fields/LangSelectField";
import RadioGroupField from "../../form-fields/RadioGroupField";
import VATField, { isCompanyVAT, validateVAT } from "../../VATField";
import { useSettingsContext } from "../../Settings/hooks/useSettingsContext";
import { SubmitButtonContext } from "../Contractation";
import { IStepForm, StepPersonFormData } from "../types/stepsFormData";
import { RootState } from "@/store";
import { zodAddressSchema } from "@/components/form-fields/AddressForm/zod/zodSchema";
import { ContractationConfigResponse } from "../types/stepsAPI";
import FileUploadForm from "@/components/form-fields/FileUploadForm/FileUploadForm";

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
.MuiTelInput-IconButton {
  display: none;
}
`;

interface FormPersonProps extends Pick<IStepForm<StepPersonFormData>, "onSubmit" | "type" | "onGoBack"> {
  showInvoicingAddress?: boolean;
  showLeadConsent?: boolean;
  showChangeOwner?: boolean;
}

const FormPerson: FC<FormPersonProps> = ({
  onSubmit,
  type,
  onGoBack,
  showInvoicingAddress = true,
  showLeadConsent = false,
  showChangeOwner = true,
}) => {
  const submitButtonContext = useContext(SubmitButtonContext);
  const Settings = useSettingsContext();
  const dniVisible = Settings?.contractation?.dniVisible ?? true;
  const dniRequired = Settings?.contractation?.dniRequired ?? true;
  const { t, i18n } = useTranslation();
  const contractation = useSelector((state: RootState) => state[`${type}Contractation`]);
  const personStep = useSelector((state: RootState) => state[`${type}Contractation`].steps.person);

  // Helper for phone prefix
  const isMobileExtensionChangeEnabled = () => {
    return Settings?.contractation?.mobileExtensionChange === true;
  };

  const removePhonePrefix = (phone: string) => {
    if (!phone) return '';
    if (isMobileExtensionChangeEnabled()) return phone;
    return !phone.startsWith("+34") ? phone : phone.slice(3);
  };

  const addInitialValuePhonePrefix = (phone: string) => {
    if (!phone) return '';
    if (isMobileExtensionChangeEnabled()) return phone;
    return phone.startsWith("+34") ? phone : '+34' + phone;
  }

  // Zod Schema Definition
  const baseSchema = z.object({
    name: z.string().min(1, t('common:text.required_field')),
    mobile: z.string().min(4, t('common:text.required_field'))
      .refine(val => {
        const valueToValidate = isMobileExtensionChangeEnabled() ? val : addInitialValuePhonePrefix(val);
        return matchIsValidTel(valueToValidate);
      }, { message: t('common:text.contractation_mobile_not_valid') }),

    email: z.string().optional()
      .refine(val => {
        const required = Settings?.contractation?.electricity?.emailRequired ?? true;
        if (required && !val) return false;
        return true;
      }, { message: t('common:text.required_field') })
      .refine(val => {
        if (val && !validEmail(val)) return false;
        return true;
      }, { message: t('common:text.contractation_email_not_valid') }),

    changeOwner: z.boolean().nullable().optional(),

    commsLanguage: z.string().optional()
      .refine(val => {
        const required = Settings.contractation?.commsLanguage ?? true;
        if (required && !val) return false;
        return true;
      }, { message: t('common:text.required_field') }),

    sameInvoiceAddress: z.boolean(),
    ownerAddress: z.any(),
    leadsConsent: z.boolean().optional().refine(
      (val) => {
        return !showLeadConsent ||
          (!Settings?.contractation?.requireLeadConsent) ||
          (Settings?.contractation?.requireLeadConsent && val)
      }, {
        message: t('common:text.required_field')
      }
    ),
    vatTokenized: z.boolean(),
  })

  const companySchema = z.discriminatedUnion("company", [
    z.object({
      company: z.literal(false),
      vat: z.string().min(1, t('common:text.required_field'))
        .refine(val => contractation.config?.vatTokenized || validateVAT(val), { message: t('common:text.vat_field_validation_invalid') }),
      surName1: z.string(),
      surName2: z.string().optional(),
      nifFaceup: z.any().optional(), // validated in superRefine
    }),
    z.object({
      company: z.literal(true),
      vat: z.string().min(1, t('common:text.required_field'))
        .refine(val => contractation.config?.vatTokenized || validateVAT(val), { message: t('common:text.vat_field_validation_invalid') })
        .superRefine((val, ctx) => {
          if (!contractation.config?.vatTokenized && Settings.contractation?.electricity?.restrictCompanyVAT) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('common:text.vat_company_not_allowed'),
            });
          }
        }),
      nifEmpresa: z.any().optional(), // validated in superRefine
      // Representative fields (optional in schema, validated in superRefine if company)
      vatRepresentante: z.string(),
      nameRepresentante: z.string(),
      surName1Representante: z.string(),
      surName2Representante: z.string().optional(),
      nifRepresentant: z.any().optional(), // validated in superRefine
    }),
  ])

  const schema = baseSchema
    .and(companySchema)
    .superRefine((data, ctx) => {
      if (data.company) {
        if (!data.vatRepresentante) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vatRepresentante'], message: t('common:text.required_field') });
        } else if (!validateVAT(data.vatRepresentante)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vatRepresentante'], message: t('common:text.vat_field_validation_invalid') });
        }

        if (!data.nameRepresentante) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nameRepresentante'], message: t('common:text.required_field') });
        if (!data.surName1Representante) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['surName1Representante'], message: t('common:text.required_field') });

        if (dniRequired && dniVisible) {
          const nifRepresentantErrors = validateFileUpload(data.nifRepresentant, {
            min: 1,
            minMessage: t('common:text.multiupload_validation_min'),
          });
          if (nifRepresentantErrors) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nifRepresentant'], message: nifRepresentantErrors });
          }

          const nifEmpresaErrors = validateFileUpload(data.nifEmpresa, {
            min: 1,
            minMessage: t('common:text.multiupload_validation_min'),
          });
          if (nifEmpresaErrors) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nifEmpresa'], message: nifEmpresaErrors });
          }
        }
      } else {
        if (!data.surName1) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['surName1'], message: t('common:text.required_field') });

        if (dniRequired && dniVisible) {
          const nifFaceupErrors = validateFileUpload(data.nifFaceup, {
            min: 1,
            minMessage: t('common:text.multiupload_validation_min'),
          });
          if (nifFaceupErrors) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nifFaceup'], message: nifFaceupErrors });
          }
        }
      }

      if (!data.sameInvoiceAddress) {
        const addressIssue = zodAddressSchema.safeParse(data.ownerAddress);
        if (!addressIssue.success) {
          addressIssue.error.issues.forEach(issue => {
            ctx.addIssue({
              ...issue,
              path: ['ownerAddress', ...(issue.path || [])]
            });
          });
        }
      }

      if (contractation.steps.installation.activeContract === true && data.changeOwner == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['changeOwner'], message: t('common:text.required_field') });
      }
    });

  const methods = useForm<StepPersonFormData>({
    shouldUnregister: false,
    resolver: zodResolver(schema),
    defaultValues: {
      ...personStep,
      ...{
        name: contractation.config.name || personStep.name,
        surName1: contractation.config.surName1 || personStep.surName1,
        surName2: contractation.config.surName2 || personStep.surName2,
        vat: contractation.config.vat || personStep.vat,
        email: contractation.config.email || personStep.email,
        mobile: isMobileExtensionChangeEnabled()
          ? (contractation.config.mobile || personStep.mobile) ?? ''
          : addInitialValuePhonePrefix((contractation.config.mobile || personStep.mobile) ?? ''),
      },
      commsLanguage: (personStep.commsLanguage || i18n.language),
      ownerAddress: personStep.ownerAddress, // Ensure ownerAddress is passed
      vatTokenized: contractation.config?.vatTokenized ?? false
    }
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = methods;

  const watchedVat = watch("vat");
  const watchedSameInvoiceAddress = watch("sameInvoiceAddress");

  useEffect(() => {
    if (isCompanyVAT(watchedVat) || (contractation.config?.vatTokenized && contractation.config.isCompanyVat)) {
      setValue("company", true);
    } else {
      setValue("company", false);
    }
  }, [watchedVat, contractation.config]);

  const showCompanyFields = watch("company");

  const handleFormSubmit = async (values: StepPersonFormData) => {
    if (showChangeOwner && !contractation.steps.installation.activeContract) {
      values.changeOwner = false;
    }
    const valuesToSubmit = { ...values };

    valuesToSubmit.mobile = removePhonePrefix(values.mobile);
    if (showInvoicingAddress && values.sameInvoiceAddress) {
      valuesToSubmit.ownerAddress = contractation.steps.installation.address;
    }

    await onSubmit(valuesToSubmit);
  };

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => handleSubmit(handleFormSubmit)(),
      loading: isSubmitting,
      disabled: contractation.leadErrors.dniInvalid || contractation.leadErrors.badScore
    });
  }, [
    isSubmitting,
    handleSubmit,
    contractation.leadErrors.dniInvalid,
    contractation.leadErrors.badScore
  ]);

  const isReadOnly = (field: keyof ContractationConfigResponse) => {
    if (contractation.config && contractation.config?.[field]) {
      return true;
    }
    return false;
  };

  const leadsNote = (
    <RichContent
      content={t('common:text.contractation_leads_note', {
        client: Settings?.organization.name ?? "",
        privacyPolicy: Settings?.links?.privacyPolicy ?? ""
      })}
    />
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>

        <Box sx={{ ml: { md: 5 }, mr: { md: 5 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} sx={{ pr: { xs: 24, sm: 0 } }}>
              <Typography variant="h6">
                {t('common:text.contractation_personal_data')}
              </Typography>
              <Controller
                name="vat"
                control={control}
                render={({ field, fieldState }) => (
                  <VATField
                    name={"vat"}
                    value={field.value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      if (isCompanyVAT(event.target.value)) {
                        setValue("company", true);
                      } else {
                        setValue("company", false);
                      }
                      field.onChange(event.target.value);
                    }}
                    error={fieldState.error?.message}
                    showError={!!fieldState.error}
                    style={{ width: "100%" }}
                    label={
                      t(
                        (Settings?.contractation?.electricity?.restrictCompanyVAT ?? false) ?
                          'common:text.contractation_nif_without_cif'
                          :
                          'common:text.contractation_nif'
                      )
                    }
                    disabled={isReadOnly("vat")}
                  />
                )}
              />
            </Grid>

            <Grid container item xs={12} spacing={3} style={{ paddingRight: 0 }}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      error={!!fieldState.error && !!fieldState.error?.message}
                      style={{ width: "100%" }}
                      type="text"
                      label={
                        showCompanyFields
                          ? t('common:text.contractation_social_reason')
                          : t('common:text.contractation_name')
                      }
                      disabled={isReadOnly("name")}
                    />
                  )}
                />
              </Grid>

              {!showCompanyFields && (
                <Grid item xs={12} md={4}>
                  <Controller
                    name="surName1"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={!!fieldState.error && !!fieldState.error?.message}
                        label={t('common:text.contractation_surname1')}
                        disabled={isReadOnly("surName1")}
                        style={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
              )}
              {!showCompanyFields && (
                <Grid item xs={12} md={4}>
                  <Controller
                    name="surName2"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={!!fieldState.error && !!fieldState.error?.message}
                        label={t('common:text.contractation_surname2')}
                        disabled={isReadOnly("surName2")}
                        style={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid container item xs={12}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="mobile"
                    control={control}
                    render={({ field, fieldState }) => (
                      <NoFlagMuiTelInputStyled
                        {...field}
                        disableDropdown={true}
                        disabled={isReadOnly("mobile")}
                        inputProps={{ maxLength: isMobileExtensionChangeEnabled() ? 16 : 12 }}
                        label={t('common:text.contractation_mobile_phone')}
                        forceCallingCode={!isMobileExtensionChangeEnabled()}
                        defaultCountry="ES"
                        fullWidth={true}
                        helperText={fieldState.error?.message}
                        error={!!fieldState.error}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field, fieldState }) => (
                      <EmailField
                        input={field}
                        meta={{ touched: !!fieldState.error, error: fieldState.error?.message }}
                        label={
                          t('common:text.contractation_email')
                          + `${(Settings?.contractation?.electricity?.emailRequired ?? true) ? "*" : ""}`
                        }
                        readOnly={isReadOnly("email")}
                        style={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Grid>

            {showCompanyFields &&
              <>
                <Grid container spacing={3} item xs={12} sx={{ pr: 0, pt: 0 }}>
                  <Grid item xs={12} sx={{ pb: { md: 0 } }}>
                    <Typography variant="h6">
                      {t('common:text.contractation_legal_representative')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4} style={{ paddingRight: 0 }}>
                    <Controller
                      name="vatRepresentante"
                      control={control}
                      render={({ field, fieldState }) => (
                        <VATField
                          name={"vatRepresentante"}
                          value={field.value}
                          onChange={field.onChange}
                          error={fieldState.error?.message}
                          showError={!!fieldState.error}
                          style={{ width: "100%" }}
                          label={t('common:text.contractation_nif')}
                          disabled={false}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="nameRepresentante"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={!!fieldState.error && !!fieldState.error?.message}
                        label={t('common:text.contractation_legal_representative_name')}
                        style={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="surName1Representante"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={!!fieldState.error && !!fieldState.error?.message}
                        label={t('common:text.contractation_legal_representative_surname1')}
                        style={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="surName2Representante"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={!!fieldState.error && !!fieldState.error?.message}
                        label={t('common:text.contractation_legal_representative_surname2')}
                        style={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
                {dniVisible && (
                  <Grid item xs={12}>
                    <Paper style={{ padding: 15 }} variant="outlined">
                      <FileUploadForm
                        key="nifRepresentant"
                        name="nifRepresentant"
                        min={1}
                        max={2}
                        label={
                          t('common:text.contractation_dni_front') + (dniRequired ? '*' : '')
                        }
                        hint={t('common:text.contractation_doc_invoice_helper', { doc: 'DNI' })}
                        anotherLabel={t('common:text.contractation_dni_back')}
                        removeLabel={t('common:text.remove')}
                      />
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </>
            }

            {showChangeOwner && contractation.steps.installation.activeContract === true &&
              <Grid item xs={12}>
                <Controller
                  name="changeOwner"
                  control={control}
                  render={({ field }) => <RadioGroupField
                    label={(Settings?.contractation?.nameInChangeOwnerQuestion ?? false) &&
                      (contractation.config.name && (contractation.config.surName1 || contractation.config.surName2)) ?
                      t("common:text.contractation_change_owner_question_named", {
                        owner: contractation.config.name + " " +
                          contractation.config.surName1 + " " +
                          contractation.config.surName2
                      })
                      : t("common:text.contractation_change_owner_question")
                    }
                    radioElements={
                      [
                        { label: t("common:text.contractation_change_owner_yes"), val: false },
                        { label: t("common:text.contractation_change_owner_no"), val: true }
                      ]
                    }
                    meta={{
                      touched: !!errors.changeOwner?.message,
                      error: errors.changeOwner?.message
                    }}
                    input={field}
                    row
                  />}
                />
              </Grid>
            }

            {(Settings.contractation?.commsLanguage ?? true) &&
              <Grid item xs={12} md={6}>
                <LangSelectField<StepPersonFormData>
                  name="commsLanguage"
                  label={t('common:text.langselect_language')}
                  readOnly={false}
                />
              </Grid>
            }

            {showInvoicingAddress && (
              <Grid item xs={12} style={{ paddingBottom: 0 }}>
                <Divider style={{ marginBottom: 10 }} />
                <Controller
                  name="sameInvoiceAddress"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CheckboxField
                      input={field}
                      meta={{ touched: !!fieldState.error, error: fieldState.error?.message }}
                      label={t('common:text.contractation_same_address')}
                    />
                  )}
                />
                {!watchedSameInvoiceAddress && (
                  <Grid item xs={12}>
                    <AddressForm
                      name="ownerAddress"
                      label={<Typography variant="subtitle1">{t('common:text.contractation_invoicing_address')}</Typography>}
                    />
                  </Grid>
                )}
              </Grid>
            )}

            {showLeadConsent &&
              Settings?.features?.leads &&
              (Settings.contractation?.showLeadConsent ?? true) && (
                <Grid item xs={12}>
                  <Controller
                    name={"leadsConsent"}
                    control={control}
                    render={({ field }) => <FormControlLabel
                      control={<Checkbox
                        title="Checkbox leads consent"
                        {...field}
                        checked={field.value ?? false}
                      />}
                      label={leadsNote}
                    />}
                  />
                  {errors.leadsConsent?.message &&
                    <FormHelperText error={true}>
                      {errors.leadsConsent.message}
                    </FormHelperText>
                  }
                </Grid>
              )}

            {dniVisible && (
              <Grid item xs={12}>
                <Divider style={{ marginBottom: 20 }} />
                <Typography variant="h6">
                  {t('common:text.contractation_doc_photos',
                    { doc: showCompanyFields ? t('common:text.contractation_doc_photo_cif') : "DNI" })
                  }
                </Typography>
                <Paper style={{ padding: 15 }} variant="outlined">
                  {showCompanyFields ?
                    <FileUploadForm
                      key="nifEmpresa"
                      name="nifEmpresa"
                      min={1}
                      max={2}
                      label={t('common:text.contractation_nif_doc') + (dniRequired ? "*" : "")}
                      hint={
                        t('common:text.contractation_doc_invoice_helper',
                          { doc: t('common:text.contractation_doc_photo_cif') })
                      }
                      anotherLabel={t('common:text.contractation_nif_add')}
                      removeLabel={t('common:text.remove')}
                    />
                    :
                    <FileUploadForm
                      key="nifFaceup"
                      name="nifFaceup"
                      min={1}
                      max={2}
                      label={
                        t('common:text.contractation_dni_front') + (dniRequired ? '*' : '')
                      }
                      hint={t('common:text.contractation_doc_invoice_helper', { doc: 'DNI' })}
                      anotherLabel={t('common:text.contractation_dni_back')}
                      removeLabel={t('common:text.remove')}
                    />
                  }
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>

        <Stack sx={{ mt: 3 }} gap={1}>
          {contractation.leadErrors.badScore &&
            <Alert severity={"error"} variant="filled">
              {t("common:text.contractation_leads_bad_score")}
              <br></br>
              {t("common:text.contractation_leads_bad_score_thanks")}
            </Alert>
          }
          {contractation.leadErrors.dniInvalid &&
            <Alert severity={"error"} variant="filled">
              {t("common:text.contractation_leads_dni_invalid")}
            </Alert>
          }
        </Stack>
      </form>
    </FormProvider>
  )
};

export default FormPerson;
